// StepFileEvidence — Step 2 檔案上傳 (requires_file=1).
// Spec: docs/specs/location-tasks.md §8.5.3.

import React, { useRef, useState } from "react";
import { Upload, Loader2, AlertTriangle, Trash2, FileText } from "lucide-react";
import { apiFetch } from "../../../lib/api-client.js";
import type { WizardCtx, WizardData } from "./types.js";
import { parseR2KeyList } from "./types.js";

interface Props {
  ctx: WizardCtx;
  data: WizardData;
  onComplete: () => void;
}

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv";
const MAX_BYTES = 10 * 1024 * 1024;

export default function StepFileEvidence({ ctx, data, onComplete }: Props): React.ReactElement {
  const [keys, setKeys] = useState<string[]>(parseR2KeyList(data.submission.file_r2_keys));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!e.target.files?.length) return;
    const files = Array.from(e.target.files);
    e.target.value = "";
    setError(null);
    setBusy(true);
    try {
      const next = [...keys];
      for (const f of files) {
        if (f.size > MAX_BYTES) {
          setError(`檔案 ${f.name} 超過 10 MB 上限。`);
          continue;
        }
        const form = new FormData();
        form.set("file", f);
        form.set("submission_id", data.submission.id);
        const r = await apiFetch(ctx.paths.uploadFile(), { method: "POST", body: form }, ctx.auth);
        const body = (await r.json().catch(() => ({}))) as { ok?: boolean; r2_key?: string; error?: string };
        if (!r.ok || !body.r2_key) {
          setError(body.error ?? `${f.name}: HTTP ${r.status}`);
          continue;
        }
        next.push(body.r2_key);
      }
      const patch = await apiFetch(
        ctx.paths.patchSubmission(data.submission.id),
        { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ file_r2_keys: next }) },
        ctx.auth,
      );
      if (!patch.ok) {
        const t = await patch.text().catch(() => "");
        setError(t || `HTTP ${patch.status}`);
        return;
      }
      setKeys(next);
      onComplete();
    } catch (e2) {
      setError((e2 as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (key: string): Promise<void> => {
    const next = keys.filter((k) => k !== key);
    const r = await apiFetch(
      ctx.paths.patchSubmission(data.submission.id),
      { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ file_r2_keys: next }) },
      ctx.auth,
    );
    if (r.ok) { setKeys(next); onComplete(); }
  };

  return (
    <div className="space-y-2">
      {keys.length > 0 && (
        <ul className="space-y-1.5">
          {keys.map((k) => (
            <li key={k} className="flex items-center gap-2 rounded-lg bg-bg-raised px-3 py-2 text-xs">
              <FileText className="h-3.5 w-3.5 text-text-secondary shrink-0" />
              <span className="font-mono truncate flex-1">{k.split("/").pop()}</span>
              <button onClick={() => void remove(k)} className="text-text-muted hover:text-danger" aria-label="刪除">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        onChange={(e) => void onPick(e)}
        className="hidden"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="block w-full h-12 rounded-xl bg-accent text-text-inverse font-semibold text-sm disabled:bg-bg-raised disabled:text-text-muted"
      >
        {busy ? (
          <span className="inline-flex items-center justify-center gap-1"><Loader2 className="h-4 w-4 animate-spin" /> 上傳中…</span>
        ) : (
          <span className="inline-flex items-center justify-center gap-1"><Upload className="h-4 w-4" /> {keys.length > 0 ? "再選檔案" : "選擇檔案"}</span>
        )}
      </button>
      <p className="text-2xs text-text-muted">支援照片 / PDF / docx / xlsx / txt / csv，每個 ≤ 10 MB。</p>

      {error && (
        <p className="text-xs text-danger inline-flex items-start gap-1">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />{error}
        </p>
      )}
    </div>
  );
}
